import React, { ReactNode, useContext, useEffect, useState } from 'react';

import AgingDashboardContext, {
  DEFAULT_FILTERS,
  FilterOption,
} from '@totem/components/ticketing/agingDashboard/AgingDashboardContext';
import {
  AgingDashboard,
  AgingFilters,
  AgingMeasure,
} from '@totem/components/ticketing/agingDashboard/types';
import { TicketingFilterOptions } from '@totem/components/ticketing/ticketing';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { TICKETS_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  children?: ReactNode;
};

const AgingDashboardContainer = ({ children }: Props) => {
  const { userProfile, getApiToken } = useContext(UserProfileContext);
  const [data, setData] = useState<AgingDashboard | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [failed, setFailed] = useState<boolean>(false);
  const [filters, setFilterState] = useState<AgingFilters>(DEFAULT_FILTERS);
  const [measure, setMeasure] = useState<AgingMeasure>('update');
  const [refreshCount, setRefreshCount] = useState<number>(0);
  const [typeOptions, setTypeOptions] = useState<FilterOption[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<FilterOption[]>([]);

  useEffect(() => {
    fetch(`${TICKETS_ENDPOINT}/filterOptions`, {
      method: 'GET',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
    })
      .then((res) => (res.ok ? res.json() : null))
      .then((result: TicketingFilterOptions | null) => {
        const toOptions = (options) =>
          (options ?? []).map((opt) => ({ value: opt.value, label: opt.name }));
        setTypeOptions(toOptions(result?.type));

        setCategoryOptions(toOptions(result?.categories));
      })
      .catch(() => {});
  }, [userProfile.organization.id, getApiToken]);

  // The API scopes to the current organization, the user's data access and internal-only tickets;
  // only these narrowing filters are sent.
  const filtersKey = JSON.stringify(
    Object.entries(filters)
      .filter(([, values]) => values.length > 0)
      .map(([field, values]) => ({ field, values })),
  );

  useEffect(() => {
    setLoading(true);

    setFailed(false);

    fetch(`${TICKETS_ENDPOINT}/ticketAging/dashboard`, {
      method: 'POST',
      headers: new Headers({ Authorization: `Bearer ${getApiToken()}` }),
      body: JSON.stringify({ filters: JSON.parse(filtersKey) }),
    })
      .then((res) => (res.ok ? res.json() : Promise.reject(res.status)))
      .then((result: AgingDashboard) => setData(result))
      .catch(() => setFailed(true))
      .finally(() => setLoading(false));
  }, [filtersKey, refreshCount, userProfile.organization.id, getApiToken]);

  return (
    <AgingDashboardContext.Provider
      value={{
        loading,
        failed,
        data,
        filters,
        setFilters: (update) =>
          setFilterState((current) => ({ ...current, ...update })),
        measure,
        setMeasure,
        typeOptions,
        categoryOptions,
        refresh: () => setRefreshCount((count) => count + 1),
      }}
    >
      {children}
    </AgingDashboardContext.Provider>
  );
};

export default AgingDashboardContainer;
